import { Button } from "components/DesignSystem/Button/Button";
import Input from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { IncorporationFormSource } from "constants/incorporation";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { useUpdateFormTreeNodeMutation } from "./useFormTreeNodeMutations";

type FormValues = {
  templateName: string;
};

type SaveStageAsTemplateModalProps = {
  isOpen: boolean;
  close: () => void;
  source: IncorporationFormSource;
  treeId: string;
  stageId: string;
};

export const SaveStageAsTemplateModal = ({
  isOpen,
  close,
  source,
  treeId,
  stageId,
}: SaveStageAsTemplateModalProps) => {
  const { alertToast, successToast } = useToast();
  const [saveStageAsTemplate, { isLoading }] = useUpdateFormTreeNodeMutation({
    source,
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await saveStageAsTemplate({
        treeId,
        body: {
          stage: {
            node_id: stageId,
            is_saved_as_template: true,
            saved_template_name: values.templateName,
          },
        },
      }).unwrap();
      successToast({ message: "Stage saved as a template" });
      close();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="small">
        <FormikForm<FormValues>
          initialValues={{ templateName: "" }}
          onSubmit={onSubmit}
        >
          {({ values }) => (
            <>
              <Modal.Body>
                <p className="t-mb-2 t-mt-1 t-text-subtext">
                  Save stage as a template?
                </p>
                <div className="t-mb-4 t-text-body-sm t-text-text-30">
                  This stage will be saved as a reusable template you can add to
                  other incorporation checklists later.
                </div>
                <Input
                  withForm
                  name="templateName"
                  label="Template name"
                  required
                  placeholder="e.g., Name approval & document preparation"
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  type="submit"
                  customType="primary"
                  isLoading={isLoading}
                  disabled={isLoading || !values.templateName}
                >
                  Save as template
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
